import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy, doc, getDoc } from 'firebase/firestore';
import { db } from '../api/firebase';
import {
  Container,
  Box,
  Typography,
  Avatar,
  Paper,
  CircularProgress,
  Alert,
  Snackbar,
  Grid,
  Button
} from '@mui/material';
import {
  Person as PersonIcon,
  RateReview as ReviewIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import ReviewListItem from '../components/ReviewListItem';

const ProfilePage = () => {
  const { user, loading: authLoading } = useAuth();
  const { t, isArabic } = useLanguage();
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [lecturers, setLecturers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteSuccess, setDeleteSuccess] = useState(false);

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/');
    }
  }, [user, authLoading, navigate]);

  // Fetch user's reviews
  useEffect(() => {
    const fetchUserReviews = async () => {
      if (!user) return;

      try {
        setLoading(true);
        setError('');

        // Query reviews by user ID
        const reviewsRef = collection(db, 'reviews');
        const q = query(
          reviewsRef,
          where('userId', '==', user.uid),
          orderBy('createdAt', 'desc')
        );

        const reviewsSnapshot = await getDocs(q);
        const userReviews = reviewsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setReviews(userReviews);

        // Fetch lecturer details for the reviews
        const lecturerIds = [...new Set(userReviews.map(review => review.professorId))];
        const lecturersData = {};

        for (const lecturerId of lecturerIds) {
          try {
            // Fetch lecturer by document ID directly
            const lecturerDocRef = doc(db, 'professors', lecturerId);
            const lecturerSnapshot = await getDoc(lecturerDocRef);
            
            if (lecturerSnapshot.exists()) {
              lecturersData[lecturerId] = {
                id: lecturerSnapshot.id,
                ...lecturerSnapshot.data()
              };
            }
          } catch (err) {
            console.error(`Error fetching lecturer ${lecturerId}:`, err);
          }
        }

        setLecturers(lecturersData);
      } catch (err) {
        console.error('Error fetching user reviews:', err);
        
        // Handle specific Firebase errors
        if (err.code === 'failed-precondition') {
          setError(t('Database index is being built. Please try again in a few minutes.', 'فهرس قاعدة البيانات قيد الإنشاء. يرجى المحاولة مرة أخرى خلال بضع دقائق.'));
        } else {
          setError(t('Failed to load your reviews. Please try again.', 'فشل في تحميل مراجعاتك. يرجى المحاولة مرة أخرى.'));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserReviews();
  }, [user, t]);

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return new Intl.DateTimeFormat(isArabic ? 'ar-SA' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  };

  // Handle review deletion
  const handleReviewDeleted = (reviewId) => {
    // Remove the review from local state
    setReviews(prevReviews => prevReviews.filter(review => review.id !== reviewId));
    
    // Show success message
    setDeleteSuccess(true);
  };

  const handleCloseSnackbar = () => {
    setDeleteSuccess(false);
  };

  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return null; // Will redirect
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Profile Header */}
      <Paper sx={{ p: 4, mb: 4, borderRadius: 2 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item>
            <Avatar
              src={user.photoURL}
              alt={user.displayName}
              sx={{ width: 80, height: 80 }}
            >
              {user.displayName?.charAt(0) || <PersonIcon />}
            </Avatar>
          </Grid>
          <Grid item xs>
            <Typography variant="h4" gutterBottom>
              {user.displayName || t('User', 'المستخدم')}
            </Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              {user.email}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('Member since', 'عضو منذ')} {formatDate(user.metadata?.creationTime)}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Reviews Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <ReviewIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h5">
            {t('My Reviews', 'مراجعاتي')} ({reviews.length})
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        ) : reviews.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <ReviewIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('No reviews yet', 'لا توجد تقييمات بعد')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('Start rating lecturers to see your reviews here', 'ابدأ بتقييم الأساتذة لرؤية مراجعاتك هنا')}
            </Typography>
            <Button 
              variant="contained" 
              onClick={() => navigate('/')}
            >
              {t('Find Lecturers', 'البحث عن أساتذة')}
            </Button>
          </Paper>
        ) : (
          <Box>
            {reviews.map((review) => {
              const lecturer = lecturers[review.professorId];
              return (
                <Box key={review.id} sx={{ mb: 3 }}>
                  <Typography 
                    variant="subtitle2" 
                    component="button"
                    onClick={() => navigate(`/lecturer/${review.professorId}`)}
                    sx={{ 
                      textAlign: isArabic ? 'right' : 'left',
                      border: 'none',
                      background: 'none',
                      color: 'primary.main',
                      cursor: 'pointer',
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' },
                      mb: 1,
                      display: 'block',
                      width: '100%',
                      fontWeight: 600,
                      fontSize: '1.1rem'
                    }}
                  >
                    {lecturer?.name || lecturer?.full_name || t('Lecturer', 'المحاضر')}
                  </Typography>
                  <ReviewListItem 
                    review={review}
                    showDelete={true}
                    currentUserId={user?.uid}
                    onReviewDeleted={handleReviewDeleted}
                  />
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      {/* Success Snackbar */}
      <Snackbar
        open={deleteSuccess}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {t('Review deleted successfully', 'تم حذف التقييم بنجاح')}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ProfilePage; 